import { sendError } from '@components/Noti';
import ViewModeSwitch from '@components/ViewModeSwitch';
import { KRP_TARGET_ID } from '@constants';
import { useTabMain } from '@hooks/paramsHooks';
import useEditingStore from '@hooks/useEditingStore';
import useToggle from '@hooks/useToggle';
import { $curScene, $tourData } from '@stores/dataStores';
import { $editingFlag } from '@stores/editingStores';
import { MainPlayer } from '@stores/krpPlayerStores';
import { $loadedIdApi } from '@stores/loadedStore';
import { $viewMode } from '@stores/viewModeStores';
import {
  type KrpPlayer,
  type SceneData,
  SceneType,
  type ViewData,
} from '@types';
import limitNum from '@utils/limitNum';
import classNames from 'classnames';
import { useStore, useStoreMap } from 'effector-react';
// @ts-ignore
import { KrpanoPlayer } from 'pano-sdk';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import {
  PM_ID_DEL,
  PM_ID_DONE,
  PM_ID_EDIT,
  onHotspotClick,
  onHotspotMoveEnd,
  onHotspotPointClick,
  onHotspotPointMoveEnd,
  onPlayerClick,
  onPolygonButtonClick,
} from '../../hotspot';
import { onFlatFovChanged, viewDefault } from '../../view';
import SceneWidgets from '../SceneWidgets';
import VideoControls from '../VideoControls';
import { onUserInteractive } from '../actions/onUserInteractive';
import { onPlayerViewChange, onViewCaptured } from '../actions/viewActions';
import loadScene from '../loadScene';

const defaultStyle: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
};

function ScenePlayer() {
  const data = useStore($curScene);
  const videoReady = useToggle(false);
  const videoPlaying = useToggle(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const tab = useTabMain();
  const [style, setStyle] = useState<React.CSSProperties>(defaultStyle);
  const viewMode = useStore($viewMode);

  const flags = useEditingStore({
    $data: $tourData,
    dataFn: (state) => state.flag,
    $editing: $editingFlag.store,
  });
  const dragMode = flags?.dragMode ?? 1;

  const canvasBgColor = useStoreMap({
    store: $tourData,
    keys: [],
    fn: (state) => state.config?.ui?.canvasBgColor || '#000000',
  });

  const showViewModeSwitch = tab === 'view' || tab === 'component';
  const mobileView = showViewModeSwitch && viewMode === 'mobile';

  useEffect(() => {
    if (mobileView) {
      const resizeHandler = () => {
        if (!containerRef.current) return;
        const height = limitNum({
          min: 550,
          max: 810,
          value: containerRef.current.clientHeight
            ? containerRef.current.clientHeight - 20
            : 0,
        });
        const width = height ? (height / 16) * 9 : 0;
        setStyle({ width, height, position: 'relative' });
      };
      resizeHandler();
      window.addEventListener('resize', resizeHandler);
      return () => {
        window.removeEventListener('resize', resizeHandler);
        setStyle(defaultStyle);
      };
    }
    setStyle(defaultStyle);
  }, [mobileView]);

  useEffect(() => {
    return () => {
      MainPlayer.remove();
      $loadedIdApi.unset();
    };
  }, []);

  useEffect(() => {
    $loadedIdApi.unset();
    videoReady.off();
    videoPlaying.off();
    if (!MainPlayer.get()) {
      const player = new KrpanoPlayer() as KrpPlayer;

      player.createPlayer(
        KRP_TARGET_ID,
        {
          onVideoReady: () => videoReady.on(),
          onVideoPlay: () => videoPlaying.on(),
          onVideoPaused: () => videoPlaying.off(),
          onVideoComplete: () => videoPlaying.off(),
          onHotspotMoveEnd,
          onHotspotClick: ({ id }) => {
            if (id === PM_ID_EDIT) {
              onPolygonButtonClick('edit');
            } else if (id === PM_ID_DONE) {
              onPolygonButtonClick('done');
            } else if (id === PM_ID_DEL) {
              onPolygonButtonClick('del');
            } else {
              const targetId = player
                .getHotspotById(id)
                .getExtendData('onClickId');
              if (targetId) {
                onHotspotClick({ id: targetId });
              }
            }
          },
          onPlayerClick,
          onHotspotPointClick,
          onHotspotPointMoveEnd,
          onFlatFovChanged,
          onViewChange: onPlayerViewChange,
          onUserInteractive,
          onSnapshootComplete: onViewCaptured,
          onSnapshootError: () => {
            sendError('播放器截图错误，请重试');
          },
        },
        () => {
          MainPlayer.register(player);
          if (data) {
            loadScene({
              player,
              data,
              options: {
                onAnimationComplete: () => {
                  if (data.type !== SceneType.matrix) {
                    player.updateView({ limitview: 'lookat' });
                  }
                  $loadedIdApi.set(data.id);
                },
              },
            });
          }
          player.controlMode({ mouse: dragMode ? 'drag' : 'moveto' });
        },
        {
          backgroundColor: canvasBgColor,
          enableSnapshoot: true,
        }
      );
    } else {
      const { view } = data ?? { view: viewDefault };
      const newViewData: ViewData = {
        ...view,
        hLookAt: tab === 'compass' ? data?.north ?? 0 : view.hLookAt,
        vLookAt: tab === 'compass' ? 0 : view.vLookAt,
      };
      const newSceneData = {
        ...data,
        view: { ...newViewData },
      } as SceneData;
      loadScene({
        player: MainPlayer.get(),
        data: newSceneData,
        options: {
          onAnimationComplete: () => {
            if (newSceneData.type !== SceneType.matrix) {
              MainPlayer.get()?.updateView({ limitview: 'lookat' });
            }
            $loadedIdApi.set(newSceneData.id);
          },
        },
      });
    }
  }, [data?.id, data?.materialId]);

  useEffect(() => {
    MainPlayer.get()?.controlMode({ mouse: dragMode ? 'drag' : 'moveto' });
  }, [dragMode]);

  return (
    <>
      <div
        className="absolute inset-0 flex items-center justify-center py-10"
        ref={containerRef}
      >
        <div
          className="relative flex items-center justify-center overflow-hidden rounded"
          style={style}
        >
          <div
            className={classNames('absolute inset-0', {
              hidden: !data,
              invisible: tab === 'compare' && data?.type === SceneType.pano,
            })}
            id={KRP_TARGET_ID}
          />
          {videoReady.isOn && (
            <VideoControls videoPlaying={videoPlaying.isOn} />
          )}
          {!!data && <SceneWidgets />}
        </div>
        {showViewModeSwitch && <ViewModeSwitch />}
      </div>
      {!data && (
        <div className="flex h-full items-center justify-center text-xl">
          请选择或添加一个场景
        </div>
      )}
    </>
  );
}

export default ScenePlayer;
